import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, AlertCircle, Info, XCircle, X } from "lucide-react";
import { cn } from "./utils";

type Variant = "default" | "success" | "error" | "warning";
interface Toast {
  id: string;
  title?: string;
  message: string;
  variant?: Variant;
  duration?: number;
}

interface ToastCtx {
  toast: (t: Omit<Toast, "id">) => void;
  success: (msg: string, title?: string) => void;
  error: (msg: string, title?: string) => void;
  info: (msg: string, title?: string) => void;
  warning: (msg: string, title?: string) => void;
  dismiss: (id: string) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const toast = useCallback((t: Omit<Toast, "id">) => {
    const id = crypto.randomUUID();
    setToasts((curr) => [...curr, { id, duration: 4500, variant: "default", ...t }]);
  }, []);

  const value = useMemo<ToastCtx>(
    () => ({
      toast,
      success: (m, t) => toast({ message: m, title: t, variant: "success" }),
      error: (m, t) => toast({ message: m, title: t, variant: "error", duration: 6000 }),
      info: (m, t) => toast({ message: m, title: t, variant: "default" }),
      warning: (m, t) => toast({ message: m, title: t, variant: "warning" }),
      dismiss,
    }),
    [toast, dismiss],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none max-w-sm w-full">
        <AnimatePresence initial={false}>
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
          ))}
        </AnimatePresence>
      </div>
    </Ctx.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  useEffect(() => {
    const t = setTimeout(() => onDismiss(toast.id), toast.duration || 4500);
    return () => clearTimeout(t);
  }, [toast.id, toast.duration, onDismiss]);

  const Icon = {
    success: CheckCircle2,
    error: XCircle,
    warning: AlertCircle,
    default: Info,
  }[toast.variant || "default"];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100, transition: { duration: 0.2 } }}
      transition={{ type: "spring", stiffness: 380, damping: 30 }}
      className={cn(
        "pointer-events-auto flex items-start gap-3 p-3.5 pr-2 rounded-xl",
        "bg-white/95 dark:bg-ink-900/95 backdrop-blur-xl border shadow-lg",
        "border-ink-200/60 dark:border-ink-800/80",
        toast.variant === "success" && "ring-1 ring-emerald-500/20",
        toast.variant === "error" && "ring-1 ring-red-500/20",
        toast.variant === "warning" && "ring-1 ring-amber-500/20",
      )}
    >
      <Icon
        className={cn(
          "size-5 shrink-0 mt-0.5",
          toast.variant === "success" && "text-emerald-500",
          toast.variant === "error" && "text-red-500",
          toast.variant === "warning" && "text-amber-500",
          toast.variant === "default" && "text-brand-500",
        )}
      />
      <div className="flex-1 min-w-0">
        {toast.title && <p className="text-sm font-medium text-ink-900 dark:text-ink-100">{toast.title}</p>}
        <p className="text-sm text-ink-700 dark:text-ink-300 text-pretty">{toast.message}</p>
      </div>
      <button
        onClick={() => onDismiss(toast.id)}
        className="shrink-0 p-1 rounded-md text-ink-500 hover:text-ink-900 hover:bg-ink-100 dark:hover:bg-ink-800 dark:hover:text-ink-100 transition-colors"
        aria-label="Dismiss"
      >
        <X className="size-3.5" />
      </button>
    </motion.div>
  );
}

export function useToast() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useToast outside ToastProvider");
  return c;
}
